import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  type ReceivedPublicBidDto,
  type SourcingEventV2Dto,
  type ViewDocumentAsOwnerDto,
  useListPublicBidsForEventQuery,
} from "../../../autogen/bff-api";
import { DocumentsAsOwnerTable } from "../../../common/documents/DocumentsAsOwnerTable";
import { useDisplayDate } from "../../../common/formatting/useDisplayDate";
import { urls } from "../../../urls";
import { isEventAwarded } from "./bidOutcome";
import { SupplierStatusTag } from "./SupplierStatusTag";
import { buildSupplierRows, isBidDeadlinePassed } from "./supplierRows";
import { competitionStage } from "./qualification/stage";

interface Props {
  event: SourcingEventV2Dto;
}

export const BidsTab = ({ event }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const displayDate = useDisplayDate();
  const eventId = event.id;
  const { data, isLoading, isError } = useListPublicBidsForEventQuery({ eventId });
  // The BFF already withholds bids until the submission deadline; we gate again on the client so a
  // server regression could never make who-submitted observable while bidding is open. Before the
  // deadline every row therefore falls back to the neutral "registered interest" state.
  const deadlinePassed = isBidDeadlinePassed(event);
  // Two-stage: before the invitation there is no tender phase at all, so the supplier table
  // (driven by interest registrations) would only mislead. Show where the competition stands.
  const stage = competitionStage(event);
  const beforeTenderPhase = stage === "REQUESTS_OPEN" || stage === "QUALIFICATION";
  const bids = useMemo(() => (deadlinePassed && data ? data.bids : []), [deadlinePassed, data]);
  const rows = useMemo(() => buildSupplierRows(event, bids), [event, bids]);
  const awarded = isEventAwarded(event.status);

  const documentsModal = useDisclosure();
  const [selectedBidId, setSelectedBidId] = useState<string | null>(null);
  const selectedBid = bids.find((bid) => bid.id === selectedBidId);
  const selectedDocuments = useMemo(
    () => (selectedBid ? getSubmittedBidDocuments(selectedBid) : []),
    [selectedBid],
  );
  const openDocuments = (bid: ReceivedPublicBidDto) => {
    setSelectedBidId(bid.id);
    documentsModal.onOpen();
  };
  const closeDocuments = () => {
    documentsModal.onClose();
    setSelectedBidId(null);
  };

  if (beforeTenderPhase) {
    return (
      <Flex justify="center" py="16">
        <Text color="smMuted">
          {t("The tender phase starts when the invitation to tender has been sent.")}
        </Text>
      </Flex>
    );
  }

  return (
    <Stack spacing="5">
      {isLoading ? (
        <Flex justify="center" py="16">
          <Spinner color="smPrimary" />
        </Flex>
      ) : isError ? (
        <Flex justify="center" py="16">
          <Text color="smError">{t("Could not load suppliers.")}</Text>
        </Flex>
      ) : rows.length === 0 ? (
        <Flex justify="center" py="16">
          <Text color="smMuted">
            {stage === "TENDERING"
              ? t("{{count}} invited suppliers — bids are revealed when the deadline passes.", {
                  count: event.publicContext.qualificationRound?.invitation?.invitedOrganizationIds.length ?? 0,
                })
              : t("No suppliers have registered interest yet.")}
          </Text>
        </Flex>
      ) : (
        <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" overflow="hidden">
          <TableContainer>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>{t("Supplier")}</Th>
                  <Th>{t("Status")}</Th>
                  {deadlinePassed && <Th>{t("Submitted")}</Th>}
                  {deadlinePassed && <Th>{t("Documents")}</Th>}
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((row) => {
                  const bid = row.bid;
                  const documents = bid ? getSubmittedBidDocuments(bid) : [];
                  const detailUrl = bid ? urls.v2.sourcingEvents.bidDetail.go(eventId, bid.id) : null;
                  return (
                    <Tr
                      key={row.organizationId}
                      _hover={{ bg: "smBackgroundSecondary" }}
                      cursor={detailUrl ? "pointer" : undefined}
                      onClick={detailUrl ? () => navigate(detailUrl) : undefined}
                    >
                      <Td fontWeight="medium" color="smText">
                        {row.organizationName || t("Unknown")}
                      </Td>
                      <Td>
                        <SupplierStatusTag bidStatus={bid?.status} awarded={awarded} size="sm" />
                      </Td>
                      {deadlinePassed && (
                        <Td color="smMuted">{bid?.submittedAt ? displayDate(bid.submittedAt) : "–"}</Td>
                      )}
                      {deadlinePassed && (
                        <Td>
                          {documents.length > 0 ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (bid) openDocuments(bid);
                              }}
                            >
                              {t("View documents")} ({documents.length})
                            </Button>
                          ) : (
                            <Text color="smMuted" fontSize="sm">
                              {t("No documents")}
                            </Text>
                          )}
                        </Td>
                      )}
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      )}
      <Modal isOpen={documentsModal.isOpen} onClose={closeDocuments} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {t("Submitted bid documents")}
            {selectedBid?.ownerName ? `: ${selectedBid.ownerName}` : ""}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb="6">
            <DocumentsAsOwnerTable documents={selectedDocuments} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Stack>
  );
};

const getSubmittedBidDocuments = (bid: ReceivedPublicBidDto): ViewDocumentAsOwnerDto[] =>
  [
    ...(bid.documents ?? []),
    ...((bid.qualificationResponses ?? []).flatMap((response) => response.evidenceDocuments ?? [])),
  ].filter((document, index, documents) => documents.findIndex((d) => d.id === document.id) === index);
